import { h } from './dom.ts';

/**
 * Table sounds (FR-FX-02): a dice rattle and a card "schwoomp", synthesised with Web Audio so
 * there are no sound files. On by default with a mute button under "Aa". The iOS app remembers the
 * choice natively (CAMPFIRE_SOUND); plain browsers keep it for the session only (no browser storage).
 */
type Native = { webkit?: { messageHandlers?: { sound?: { postMessage(v: string): void } } }; CAMPFIRE_SOUND?: string };
const g = globalThis as Native;
let enabled = g.CAMPFIRE_SOUND !== 'off';
let audio: AudioContext | null = null;

/** Browsers only allow sound after a tap; the first tap anywhere unlocks it. */
function context(): AudioContext | null {
  if (!enabled) return null;
  const Ctor = (globalThis as { AudioContext?: typeof AudioContext }).AudioContext;
  if (!Ctor) return null;
  audio ??= new Ctor();
  if (audio.state === 'suspended') void audio.resume().catch(() => {});
  return audio.state === 'running' ? audio : null;
}
addEventListener('pointerdown', () => void context(), { capture: true });

function noise(a: AudioContext, seconds: number): AudioBufferSourceNode {
  const buf = a.createBuffer(1, Math.max(1, Math.floor(a.sampleRate * seconds)), a.sampleRate);
  const data = buf.getChannelData(0);
  for (let i = 0; i < data.length; i++) data[i] = Math.random() * 2 - 1;
  const src = a.createBufferSource();
  src.buffer = buf;
  return src;
}

/** A short filtered click: one die hitting another. */
function click(a: AudioContext, at: number, freq: number, level: number): void {
  const src = noise(a, 0.03);
  const band = a.createBiquadFilter();
  band.type = 'bandpass';
  band.frequency.value = freq;
  band.Q.value = 6;
  const gain = a.createGain();
  gain.gain.setValueAtTime(level, at);
  gain.gain.exponentialRampToValueAtTime(0.001, at + 0.03);
  src.connect(band).connect(gain).connect(a.destination);
  src.start(at);
}

/** Dice rattling in a cup and landing (about as long as the tumble). */
export function rattle(): void {
  const a = context();
  if (!a) return;
  const t = a.currentTime;
  for (let i = 0; i < 14; i++) click(a, t + i * 0.045 + Math.random() * 0.02, 1800 + Math.random() * 2200, 0.25 + Math.random() * 0.2);
  for (let i = 0; i < 4; i++) click(a, t + 0.66 + i * 0.03, 900 + Math.random() * 600, 0.35);
}

/** A card sliding across the table, `delay` seconds from now. */
export function schwoomp(delay = 0): void {
  const a = context();
  if (!a) return;
  const t = a.currentTime + delay;
  const src = noise(a, 0.18);
  const band = a.createBiquadFilter();
  band.type = 'bandpass';
  band.Q.value = 1.2;
  band.frequency.setValueAtTime(3500, t);
  band.frequency.exponentialRampToValueAtTime(700, t + 0.16);
  const gain = a.createGain();
  gain.gain.setValueAtTime(0.0001, t);
  gain.gain.exponentialRampToValueAtTime(0.35, t + 0.03);
  gain.gain.exponentialRampToValueAtTime(0.001, t + 0.17);
  src.connect(band).connect(gain).connect(a.destination);
  src.start(t);
}

export const soundOn = (): boolean => enabled;

/** A floating speaker button under "Aa" that turns table sounds on and off. */
export function mountSoundButton(): void {
  const label = () => (enabled ? 'Sounds on' : 'Sounds off');
  const button = h('button.soundtoggle', { 'data-testid': 'sound', 'aria-label': label(), 'aria-pressed': String(enabled) }, enabled ? '🔊' : '🔇') as HTMLButtonElement;
  button.addEventListener('click', () => {
    enabled = !enabled;
    button.textContent = enabled ? '🔊' : '🔇';
    button.setAttribute('aria-label', label());
    button.setAttribute('aria-pressed', String(enabled));
    g.webkit?.messageHandlers?.sound?.postMessage(enabled ? 'on' : 'off');
    if (enabled) schwoomp();
  });
  document.body.append(button);
}
